// Built-in & External Libraries
import React, { useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FloatButton } from 'antd';
import { Toaster } from 'react-hot-toast';
import { Transition } from 'react-transition-group';
import { AnimatePresence } from 'framer-motion';


import './App.css';
import { AccountingWebSocketProvider } from '../components/pages/AccountingPages/AccountingWebSocket';
import AppWebSocketManager from '../components/AppWebSocketManager';
import { BackendWebSocketProvider } from '../components/BackendWebSocket';
import HashWindowManager from '../components/HashWindowManager';
import MainNavigation from '../components/MainNavigation';
import ScreenExtensionBlockerWindow from '../components/ScreenExtensionBlockerWindow';
import BalancePageHeader from '../components/pages/TransactionsPage/BalancePageHeader';
import SystemModals from '../components/SystemModals';
import SystemDataPreloader from '../components/SystemDataPreloader';
import UserCompanyInitializer from '../components/UserCompanyInitializer';
import {useIsAuthenticated, useIsEmployeeLoaded, useIsExpenseAgentAvailable, useWindowExtensionBlock} from '../hooks';
import { StarsIcon } from '../icons';
import { mainActions } from '../state/actions/main.actions';
import { systemHelpers } from '../utils/helpers';
import { history } from '../state/history';
import routesList from '../routes/routes.json';
import { StyledApp, StyledContentContainer, StyledNavContainer, StyledSpin } from './StyledApp';
import { containerStyles } from './containerStyles';
import Routes from '../routes/Routes';
import { SYSTEM_ANIMATION_DURATION } from '../constants';
import {colorsStyles} from "../utils/styles";
import IconWrapper from "../components/IconWrapper";


const publicPaths = systemHelpers.getPublicPaths();

const pageContainerStyles = containerStyles(SYSTEM_ANIMATION_DURATION);

const isEnablePath = () => {
  const paths = [...publicPaths, routesList.invite, routesList.verification];
  let isEnable = false;

  paths.forEach(path => {
    if (history.location.pathname.startsWith(path)) {
      isEnable = true;
    }
  });
  return isEnable;
}


const App = () => {
  const dispatch = useDispatch();

  const loading = useSelector(state => state.main.loading);
  const isAuthenticated = useIsAuthenticated();
  const initialLoading = useSelector(state => state.main.initialLoading);
  const isOpenMenu = useSelector(state => state.main.isOpenMenu);

  const [isDisabledDataLoading, setIsDisabledDataLoading] = useState(false);
  const isEmployeeLoaded = useIsEmployeeLoaded();
  const navigationNodeRef = useRef(null);
  const containerNodeRef = useRef(null);

  const { isBlocked: isBlockedScreen } = useWindowExtensionBlock(767);
  const [t] = useTranslation('main');

  const isExpenseAgentAvailable = useIsExpenseAgentAvailable();

  const mainLoading = useMemo(() => {
    return !(isDisabledDataLoading ? true : !initialLoading && (isAuthenticated ? isEmployeeLoaded : true))
  }, [isDisabledDataLoading, initialLoading, isAuthenticated, isEmployeeLoaded]);

  const isEnabledBlockedScreen = useMemo(() => isBlockedScreen && !isEnablePath(), [isBlockedScreen]);

  // Flips to true the first time the app renders at a correct screen size.
  // Once content has loaded, a later shrink must not unmount (and lose) it.
  const hasLoadedContentRef = useRef(false);
  if (!isEnabledBlockedScreen) {
    hasLoadedContentRef.current = true;
  }

  const handleDisableDataLoading = () => setIsDisabledDataLoading(true);

  const blockerDescription = (
    <>
      {t('messages.screenExtensionBlockerDescription1')}<br/>
      {t('messages.screenExtensionBlockerDescription2')}<br/>
      {t('messages.screenExtensionBlockerDescription3')}
    </>
  );
  const blockerTitle = t('messages.screenExtensionBlockerTitle');

  // Bad screen size before any content loaded: replace the whole app.
  // As soon as the size is corrected the app becomes available; a later
  // shrink is handled by the overlay below instead of a full replace.
  if (isEnabledBlockedScreen && !hasLoadedContentRef.current) {
    return (
      <ScreenExtensionBlockerWindow
        description={blockerDescription}
        title={blockerTitle}
      />
    );
  }

  const routes = <Routes isAuth={isAuthenticated} />;

  return (
    <StyledApp className='App'>
      <StyledSpin
        className='white'
        spinning={mainLoading}
        wrapperClassName='full-width-spin'
      >
        <StyledSpin
          spinning={loading}
          wrapperClassName='full-width-spin'
        >
          <AnimatePresence exitBeforeEnter>
            <Router history={history} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <BackendWebSocketProvider>
                <AccountingWebSocketProvider>

                  {/* Routes for not auth user */}

                  {!isAuthenticated && routes}

                  {/* View, Navigation & Routes for auth user */}

                  {isAuthenticated && (
                    <div className='d-flex flex-row'>
                      <Transition nodeRef={navigationNodeRef} in={isOpenMenu} timeout={SYSTEM_ANIMATION_DURATION}>
                        {state => (
                          <StyledNavContainer
                            ref={navigationNodeRef}
                            style={pageContainerStyles.navigation[state]}
                          >
                            <MainNavigation />
                          </StyledNavContainer>
                        )}
                      </Transition>
                      <Transition nodeRef={containerNodeRef} in={!isOpenMenu} timeout={SYSTEM_ANIMATION_DURATION}>
                        {state => (
                          <StyledContentContainer
                            id='app-content-container'
                            ref={containerNodeRef}
                            style={pageContainerStyles.container[state]}
                          >
                            <div>
                              <BalancePageHeader />
                            </div>
                            {routes}
                          </StyledContentContainer>
                        )}
                      </Transition>

                      <Toaster position='bottom-right' />

                    </div>
                  )}

                  <HashWindowManager />

                  {/* Initialize company and user */}
                  <UserCompanyInitializer handleDisableDataLoading={handleDisableDataLoading} />

                  <AppWebSocketManager />

                </AccountingWebSocketProvider>
              </BackendWebSocketProvider>
            </Router>
          </AnimatePresence>
        </StyledSpin>
      </StyledSpin>

      {isAuthenticated && (
        <>
          {/* Component with modal windows  */}
          <SystemModals />

          {isExpenseAgentAvailable && (
            <FloatButton
              tooltip={t('messages.expenseAgentTooltip')}
              shape='circle'
              style={{ insetInlineEnd: 24, insetBlockEnd: 24 }}
              icon={<IconWrapper size={20} color={colorsStyles.additional_purple}><StarsIcon /></IconWrapper>}
              onClick={() => dispatch(mainActions.openFullWidthAgentChat())}
            />
          )}
        </>
      )}

      {/* Load information to correct system work */}
      <SystemDataPreloader />

      {/* User shrank the window after load: overlay the blocker, keep content mounted */}
      {isEnabledBlockedScreen && (
        <ScreenExtensionBlockerWindow
          isOverlay
          description={blockerDescription}
          title={blockerTitle}
        />
      )}

    </StyledApp>
  );
}

export default App;
